import { useElementSize } from '@helpers/hooks/use-element-size/useElementSize';
import * as d3 from 'd3';
import { FC, JSX, useEffect, useMemo, useRef } from 'react';

import {
  ChartXAxis,
  ChartXAxisCalendarStep,
} from '../../components/chart-x-axis/ChartXAxis';
import { getChartXAxisTicks } from '../../components/chart-x-axis/chart-x-axis-ticks';
import { humanFriendlyTicks } from '../helpers/human-friendly-ticks/human-friendly-ticks';
import { HorizontalGridLines } from '../high-low-triple-line-chart/components/horizontal-grid-lines/HorizontalGridLines';
import { LinePlot } from '../high-low-triple-line-chart/components/line-plot/LinePlot';
import {
  BAND_CHART_MARGIN_BOTTOM,
  BAND_CHART_RIGHT_PADDING,
} from './BandChart.constants';
import styles from './BandChart.module.scss';
import { Scale, SpxTsData } from './BandChart.types';
import { SectionArea } from './section-area/SectionArea';
import { drawThresholdFill } from './section-utils/section-utils';

const Y_TICK_COUNT = 7;

export interface BandChartProps {
  /** Time-series data points to plot. */
  tsData: SpxTsData[];
  /** Optional render-prop for SVG overlays drawn on top of the band; receives the chart's d3 scales. */
  renderAfterPlotNode?: (
    xScale: Scale<number>,
    yScale: Scale<number>
  ) => JSX.Element;
  /** Show the line connecting the data points on top of the filled band. */
  showLine?: boolean;
  /** Clamp the y-domain to the data extent. Defaults to `true`. */
  domainLimited?: boolean;
  /** Add right-side padding so the last point isn't flush with the edge. Defaults to `true`. */
  includeXScalePadding?: boolean;
  /** Render the chart with a bordered frame instead of the default borderless plot. */
  isBordered?: boolean;
  /** Fill colors above and below `threshold`. Triggers the canvas-based threshold-fill rendering when set. */
  fillColors?: {
    positive: string;
    negative: string;
    singlePointPositive: string;
    singlePointNegative: string;
  };
  /** Y-axis value used as the divider for `fillColors`. Defaults to `0`. */
  threshold?: number;
  /** Format y-axis ticks as percentages (`12.3%` vs `12.30`). */
  isPercent?: boolean;
  /** Locale string forwarded to the x-axis date formatter. */
  locale?: string;
  /** Calendar step used to label the x-axis. Defaults to `'month'`. */
  xAxisCalendarStep?: ChartXAxisCalendarStep;
}

/**
 * Filled-area "band" time-series chart. Renders a single value over
 * time with an optional line on top and a two-color fill split by a
 * `threshold` (e.g. red below, green above).
 *
 * Built on d3 + an inline `<canvas>` for the threshold fill so it
 * stays performant on large series.
 */
export const BandChart: FC<BandChartProps> = ({
  tsData,
  renderAfterPlotNode,
  showLine,
  domainLimited = true,
  includeXScalePadding = true,
  isBordered,
  fillColors,
  threshold = 0,
  isPercent,
  locale,
  xAxisCalendarStep = 'month',
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const { width, height } = useElementSize(svgRef);

  const adjustedWidth = width - BAND_CHART_RIGHT_PADDING;
  const heightOffset = height * 0.05;

  const [minY = 0, maxY = 0] = useMemo(
    () => d3.extent(tsData.map(p => p.value)),
    [tsData]
  );

  const isSinglePoint = tsData.length === 1;

  // One value yields a degenerate y domain (minY === maxY). Pad it only for
  // the single-point case so the dot sits mid-chart and y ticks stay finite;
  // multi-point series keep their exact original domain.
  const [yMin, yMax] = useMemo<[number, number]>(() => {
    if (isSinglePoint) {
      const pad = Math.abs(minY) * 0.1 || 1;
      return [minY - pad, maxY + pad];
    }
    return [minY, maxY];
  }, [isSinglePoint, minY, maxY]);

  const yTicks = useMemo(
    () => humanFriendlyTicks([yMin, yMax], Y_TICK_COUNT, true).ticks,
    [yMin, yMax]
  );

  const xScale = useMemo(() => {
    const domainEnd = includeXScalePadding
      ? tsData.length + 1
      : Math.max(tsData.length - 1, 1);
    return d3
      .scaleLinear()
      .domain([0, domainEnd])
      .range([0, adjustedWidth || 0]);
  }, [tsData, adjustedWidth, includeXScalePadding]);

  const { tickValues, dateScale } = useMemo(
    () =>
      getChartXAxisTicks({
        tsData,
        xRange: xScale.range(),
        domainLimited,
        calendarStep: xAxisCalendarStep,
      }),
    [tsData, xScale, domainLimited, xAxisCalendarStep]
  );

  const hasXAxis = tickValues.length > 0;

  const yScale = useMemo(() => {
    return d3
      .scaleLinear()
      .domain([yTicks[0], yTicks[yTicks.length - 1]])
      .range([
        hasXAxis
          ? height - BAND_CHART_MARGIN_BOTTOM + heightOffset
          : height - heightOffset,
        heightOffset,
      ]);
  }, [height, hasXAxis, heightOffset, yTicks]);

  useEffect(() => {
    if (!canvasRef.current || tsData.length < 2 || !fillColors) {
      return;
    }
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }

    drawThresholdFill(
      ctx,
      tsData,
      xScale,
      yScale,
      adjustedWidth,
      height,
      threshold,
      fillColors
    );
  }, [
    tsData,
    xScale,
    yScale,
    adjustedWidth,
    height,
    canvasRef.current,
    threshold,
    fillColors,
  ]);

  if (!tsData.length) {
    return null;
  }

  const plotBottom = yScale.range()[0];
  const singlePoint = isSinglePoint ? tsData[0] : undefined;
  const singlePointColor =
    singlePoint?.value !== undefined && singlePoint.value > 0
      ? fillColors?.singlePointPositive
      : fillColors?.singlePointNegative;

  return (
    <div className={styles.bandChartRoot}>
      <svg className={styles.plot} data-testid="band-chart" ref={svgRef}>
        <foreignObject width={width} height={height} x="0" y="0">
          <canvas ref={canvasRef} width={width} height={height} />
        </foreignObject>
        <g>
          {!isSinglePoint && (
            <SectionArea ts={tsData} scaleX={xScale} height={plotBottom} />
          )}
          <g transform={`translate(${adjustedWidth}, 0)`}>
            <HorizontalGridLines
              yScale={yScale}
              tickSize={adjustedWidth}
              tickCount={Y_TICK_COUNT}
              tickValues={yTicks}
              format={(val, fractionDigits = 1) => {
                return isPercent
                  ? `${val.toFixed(fractionDigits)}%`
                  : val.toFixed(2);
              }}
              rightAxisWidth={BAND_CHART_RIGHT_PADDING}
            />
          </g>

          <ChartXAxis
            tickValues={tickValues}
            dateScale={dateScale}
            yScale={yScale}
            showLine={showLine}
            padding={0}
            y1={-maxY}
            adjustedWidth={adjustedWidth}
            isBordered={isBordered}
            locale={locale}
            calendarStep={xAxisCalendarStep}
          />
          <g>
            <LinePlot
              className={styles.linePlot}
              data-band-chart-line-plot
              ts={tsData}
              scaleX={xScale}
              scaleY={yScale}
            />
            {singlePoint && (
              <circle
                cx={6}
                cy={yScale(singlePoint.value)}
                r="4"
                fill={singlePointColor}
                stroke={singlePointColor}
                data-testid="single-point"
              />
            )}
          </g>
          {!!renderAfterPlotNode && renderAfterPlotNode(xScale, yScale)}
        </g>
      </svg>
    </div>
  );
};

export {
  BAND_CHART_DEFAULT_PLOT_HEIGHT_PX,
  BAND_CHART_MARGIN_BOTTOM,
  BAND_CHART_RIGHT_PADDING,
} from './BandChart.constants';
